
import React, { useState } from 'react';
import { usePackageExclusions } from '@/hooks/usePackageExclusions';
import { PackageItem } from '@/types/package-item';
import { PackageExclusionCard } from '@/components/package-exclusions/PackageExclusionCard';
import { PackageExclusionDialog } from '@/components/package-exclusions/PackageExclusionDialog';
import { Button } from '@/components/ui/button';
import { PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ViewToggle } from '@/components/shared/ViewToggle';
import { PackageExclusionsTable } from '@/components/package-exclusions/PackageExclusionsTable';


const PackageExclusions = () => {
  const { exclusions, isLoading, error, addExclusion, updateExclusion, deleteExclusion } = usePackageExclusions();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PackageItem | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [view, setView] = useState<'table' | 'cards'>('cards');

  const handleAddNew = () => {
    setSelectedItem(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (item: PackageItem) => {
    setSelectedItem(item);
    setIsDialogOpen(true);
  };

  const handleDeleteRequest = (id: string) => {
    setItemToDelete(id);
    setIsDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setIsSaving(true);
    try {
      await deleteExclusion(itemToDelete);
      toast.success("تم حذف البند بنجاح");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "فشل حذف البند";
      toast.error(errorMessage);
      console.error(err);
    } finally {
        setItemToDelete(null);
        setIsDeleteDialogOpen(false);
        setIsSaving(false);
    }
  };

  const handleSave = async (values: { text_ar: string; text_en: string; }) => {
    setIsSaving(true);
    try {
      if (selectedItem) {
        await updateExclusion({ id: selectedItem.id, ...values });
        toast.success("تم تحديث البند بنجاح");
      } else {
        await addExclusion(values);
        toast.success("تمت إضافة البند بنجاح");
      }
      setIsDialogOpen(false);
      setSelectedItem(null);
    } catch (err) {
      const defaultMessage = selectedItem ? "فشل تحديث البند" : "فشل إضافة البند";
      const errorMessage = err instanceof Error ? err.message : defaultMessage;
      toast.error(errorMessage);
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (error) {
    return <div className="text-center text-red-500 p-6">حدث خطأ: {error.message}</div>;
  }

  return (
    <div className="container mx-auto p-6" dir="rtl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">إدارة بنود "البرنامج لا يشمل"</h1>
        <div className="flex items-center gap-4">
          <ViewToggle view={view} onViewChange={setView} />
          <Button onClick={handleAddNew}>
            <PlusCircle className="ml-2 h-4 w-4" />
            إضافة بند جديد
          </Button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-[150px] w-full" />)}
        </div>
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {exclusions?.map((item) => (
            <PackageExclusionCard
              key={item.id}
              item={item}
              onEdit={() => handleEdit(item)}
              onDelete={() => handleDeleteRequest(item.id)}
            />
          ))}
        </div>
      ) : (
        <PackageExclusionsTable
          items={exclusions || []}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
        />
      )}

      {exclusions?.length === 0 && !isLoading && (
        <div className="text-center py-12 text-gray-500">
          <p>لم يتم العثور على أي بنود.</p>
          <p>انقر على "إضافة بند جديد" للبدء.</p>
        </div>
      )}

      <PackageExclusionDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        item={selectedItem}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>هل أنت متأكد؟</AlertDialogTitle>
            <AlertDialogDescription>
              هذا الإجراء سيقوم بحذف البند نهائياً ولا يمكن التراجع عنه.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} disabled={isSaving} className="bg-red-600 hover:bg-red-700">
              {isSaving ? 'جاري الحذف...' : 'حذف'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PackageExclusions;
